import React, { useEffect, useState } from "react";
import {
  getNotificationSettings,
  updateNotificationSettings,
} from "../../api/settings.js";
import { useDispatch, useSelector } from "react-redux";
import ToggleSwitch from "../../components/ToggleSwitch.jsx";
import { addToast } from "../../store/app/action.js";
import Loader from "../../components/Loader.jsx";
import { PrimaryButton, SecondaryButton } from "../../components/Buttons.jsx";
import { formatPhoneNumber } from "../../utils/Utils.js";
import CanView from "@/components/CanView.jsx";
const orderSettings = {
  new_order: {
    title: "New Order",
    description: "Receive a notification when a new order is received.",
    unavailable: [],
  },
  order_reminder: {
    title: "Order Reminder",
    description:
      "Receive a notification when an order has been pending for longer than 5 minutes.",
    unavailable: ["email"],
  },
};

function NotificationsPanel() {
  const { selectedLocation } = useSelector((state) => state.app);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const [settings, setSettings] = useState({});
  const [orderContacts, setOrderContacts] = useState({});

  const submit = async () => {
    const orderKeys = Object.keys(orderSettings);
    const settingKeys = Object.keys(settings);
    const _orderContacts = {
      email: orderContacts.email.trim().toLowerCase(),
      phone_number: formatPhoneNumber(orderContacts.phone_number),
      sms_phone_number: formatPhoneNumber(orderContacts.sms_phone_number),
    };
    setOrderContacts(_orderContacts);
    for (let key of settingKeys) {
      if (orderKeys.includes(key)) {
        settings[key].email = _orderContacts.email;
        settings[key].phone_number = _orderContacts.phone_number;
        settings[key].sms_phone_number = _orderContacts.sms_phone_number;

        // if email is enabled but no email address is provided throw warning
        if (settings[key].email_enabled && !settings[key].email) {
          dispatch(
            addToast({
              type: "error",
              message:
                "Please provide an email address for email notifications",
              timer: 5,
            })
          );
          return;
        }
        if (settings[key].phone_enabled && !settings[key].phone_number) {
          dispatch(
            addToast({
              type: "error",
              message:
                "Please provide a valid phone number for phone call notifications",
              timer: 5,
            })
          );
          return;
        }
        if (settings[key].sms_enabled && !settings[key].sms_phone_number) {
          dispatch(
            addToast({
              type: "error",
              message:
                "Please provide a valid phone number for sms notifications",
              timer: 5,
            })
          );
          return;
        }
      }
    }

    // turn back into array
    const settingsArray = Object.values(settings);
    setProcessing(true);
    try {
      await updateNotificationSettings({ settings: settingsArray });
      dispatch(
        addToast({
          type: "success",
          message: "Notification settings updated successfully",
          timer: 5,
        })
      );
    } catch (e) {
      dispatch(
        addToast({
          type: "error",
          message: "An error occurred while updating notification settings",
          timer: 5,
        })
      );
    } finally {
      setProcessing(false);
    }
  };

  const _getSettings = async () => {
    setLoading(true);
    getNotificationSettings().then((res) => {
      const map = {};
      for (let setting of res.settings) {
        map[setting.event] = setting;
      }
      const contacts = { phone_number: "", sms_phone_number: "", email: "" };
      Object.keys(orderSettings).forEach((key) => {
        if (!map[key]) {
          map[key] = {
            event: key,
            email_enabled: false,
            sms_enabled: false,
            phone_enabled: false,
          };
        } else {
          if (map[key].email) {
            contacts.email = map[key].email;
          }
          if (map[key].phone_number) {
            contacts.phone_number = map[key].phone_number;
          }
          if (map[key].sms_phone_number) {
            contacts.sms_phone_number = map[key].sms_phone_number;
          }
        }
      });
      setOrderContacts(contacts);
      setSettings(map);
      setLoading(false);
    });
  };

  useEffect(() => {
    if (selectedLocation) {
      _getSettings();
    }
  }, [selectedLocation]);

  return (
    <div className="grow">
      {/* Panel body */}
      {loading && <Loader />}
      {!loading && (
        <>
          <div className="p-6 space-y-6">
            <h2 className="text-2xl text-slate-800 dark:text-slate-100 font-bold mb-5">
              My Notifications
            </h2>

            {/* General */}
            <section>
              <h3 className="text-xl leading-snug text-slate-800 dark:text-slate-100 font-bold mb-1">
                Orders
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
                {/* Email Section */}
                <div className="space-y-4">
                  <div>
                    <div className="font-bold text-slate-800 dark:text-slate-100">Email</div>
                    <input
                      type="email"
                      placeholder="Enter email address"
                      className="form-input mt-2 w-full"
                      value={orderContacts.email}
                      onChange={(e) =>
                        setOrderContacts({
                          ...orderContacts,
                          email: e.target.value,
                        })
                      }
                    />
                  </div>
                  
                  {/* Email Toggles */}
                  {Object.keys(orderSettings).map((key) => {
                    if (orderSettings[key].unavailable.includes("email")) return null;
                    const emailDescription = key === 'new_order' 
                      ? "Receive an email notification when a new order is received."
                      : "Receive an email notification when an order has been pending for longer than 5 minutes.";
                    return (
                      <div key={`email-${key}`} className="space-y-2">
                        <div className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          {orderSettings[key].title}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">
                          {emailDescription}
                        </div>
                        <ToggleSwitch
                          checked={settings?.[key]?.email_enabled}
                          onChange={(val) => {
                            setSettings({
                              ...settings,
                              [key]: {
                                ...settings[key],
                                email_enabled: val,
                              },
                            });
                          }}
                          onLabel="Enabled"
                          offLabel="Disabled"
                        />
                      </div>
                    );
                  })}
                </div>

                {/* SMS Section */}
                <div className="space-y-4">
                  <div>
                    <div className="font-bold text-slate-800 dark:text-slate-100">SMS</div>
                    <input
                      type="tel"
                      placeholder="Enter phone number"
                      className="form-input mt-2 w-full"
                      value={orderContacts.sms_phone_number}
                      onChange={(e) =>
                        setOrderContacts({
                          ...orderContacts,
                          sms_phone_number: e.target.value,
                        })
                      }
                    />
                  </div>
                  
                  {/* SMS Toggles */}
                  {Object.keys(orderSettings).map((key) => {
                    if (orderSettings[key].unavailable.includes("sms")) return null;
                    const smsDescription = key === 'new_order' 
                      ? "Receive an SMS text message when a new order is received."
                      : "Receive an SMS text message when an order has been pending for longer than 5 minutes.";
                    return (
                      <div key={`sms-${key}`} className="space-y-2">
                        <div className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          {orderSettings[key].title}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">
                          {smsDescription}
                        </div>
                        <ToggleSwitch
                          checked={settings?.[key]?.sms_enabled}
                          onChange={(val) => {
                            setSettings({
                              ...settings,
                              [key]: {
                                ...settings[key],
                                sms_enabled: val,
                              },
                            });
                          }}
                          onLabel="Enabled"
                          offLabel="Disabled"
                        />
                      </div>
                    );
                  })}
                </div>

                {/* Phone Call Section */}
                <div className="space-y-4">
                  <div>
                    <div className="font-bold text-slate-800 dark:text-slate-100">Phone Call</div>
                    <input
                      type="tel"
                      placeholder="Enter phone number"
                      className="form-input mt-2 w-full"
                      value={orderContacts.phone_number}
                      onChange={(e) =>
                        setOrderContacts({
                          ...orderContacts,
                          phone_number: e.target.value,
                        })
                      }
                    />
                  </div>
                  
                  {/* Phone Call Toggles */}
                  {Object.keys(orderSettings).map((key) => {
                    if (orderSettings[key].unavailable.includes("phone")) return null;
                    const phoneDescription = key === 'new_order' 
                      ? "Receive a phone call when a new order is received."
                      : "Receive a phone call when an order has been pending for longer than 5 minutes.";
                    return (
                      <div key={`phone-${key}`} className="space-y-2">
                        <div className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          {orderSettings[key].title}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">
                          {phoneDescription}
                        </div>
                        <ToggleSwitch
                          checked={settings?.[key]?.phone_enabled}
                          onChange={(val) => {
                            setSettings({
                              ...settings,
                              [key]: {
                                ...settings[key],
                                phone_enabled: val,
                              },
                            });
                          }}
                          onLabel="Enabled"
                          offLabel="Disabled"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          </div>

          {/* Panel footer */}
          <footer>
            <div className="flex flex-col px-6 py-5 border-t border-slate-200 dark:border-slate-700">
              <div className="flex self-end space-x-4">
                <PrimaryButton onClick={submit} loading={processing}>
                  Save Changes
                </PrimaryButton>
              </div>
            </div>
          </footer>
        </>
      )}
    </div>
  );
}

export default NotificationsPanel;
